import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCheck, Loader2 } from 'lucide-react'
import type { Notificacao } from '@/lib/types'
import { listNotificacoes, marcarLida, marcarTodasLidas } from '@/services/notificacoes'

export default function NotificacoesPage() {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      setNotificacoes(await listNotificacoes())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { carregar() }, [carregar])

  async function abrir(n: Notificacao) {
    if (!n.lida_em) await marcarLida(n.id)
    if (n.link) navigate(n.link)
    else await carregar()
  }

  async function todasLidas() {
    await marcarTodasLidas()
    await carregar()
  }

  const naoLidas = notificacoes.filter(n => !n.lida_em).length

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Notificações</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>
            {naoLidas > 0 ? `${naoLidas} não lida${naoLidas > 1 ? 's' : ''}` : 'Tudo lido.'}
          </p>
        </div>
        {naoLidas > 0 && (
          <button className="btn-secondary" onClick={todasLidas}>
            <CheckCheck size={15} aria-hidden /> Marcar todas como lidas
          </button>
        )}
      </header>

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        </div>
      ) : notificacoes.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Bell size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhuma notificação</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notificacoes.map(n => (
            <button
              key={n.id}
              onClick={() => abrir(n)}
              className="card"
              style={{
                padding: '13px 16px', textAlign: 'left', border: '1px solid var(--border)',
                background: n.lida_em ? 'var(--card)' : 'var(--primary-soft)',
                display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%',
              }}
            >
              {!n.lida_em && <span aria-label="não lida" style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--primary)', marginTop: 6, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: n.lida_em ? 500 : 700, fontSize: 14 }}>{n.titulo}</div>
                {n.corpo && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{n.corpo}</div>}
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                  {new Date(n.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
